import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { IPatientInfo } from '../../patient/ipatient-info';
import { environment } from 'src/environments/environment';
import { GenderList } from 'src/app/common/types/common';
import { IHospitalInfo } from '../../hospital/ihospital-info';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Iopd } from '../../opd/opddetails/iopddetails';
import { Iipd } from '../Iipd.type';
import { DatePipe } from '@angular/common';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { HttpClient } from '@angular/common/http';
import { UserService } from '../../user/user.service';
import { IUsers } from '../../user/iusers';
import { MedicalCertificateService } from './medical-certificate.service';
import { ToastrService } from 'ngx-toastr';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { Subject, takeUntil } from 'rxjs';
import { IMedicalCertificate } from './imedical.type';
import { PrintSettingService } from '../../settings/hospital-setting/print-setting/print-setting.service';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';

@Component({
  selector: 'app-medical-certificate',
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule,
    ReactiveFormsModule
  ],
  providers: [
    DatePipe,
    DefaultValuePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './medical-certificate.component.html',
  styleUrl: './medical-certificate.component.scss'
})
export class MedicalCertificateComponent implements OnInit {
  patientinfo!: IPatientInfo;
  staticimagepath: string = environment.imagePath;
  defaultAvatarPath = 'assets/images/profile/user-1.jpg';
  hospitalInfo: IHospitalInfo;
  certificateForm: FormGroup;
  opdDetails: Iopd;
  ipdData: Iipd;
  showPreview = false;
  pronouns: { subject: string } = { subject: 'He/She' }; // Add pronouns object
  showPatientInfo: boolean = false;
  doctorName: string = '';
  doctorDetails: IUsers;
  isSaved: boolean = false;
  private _unsubscribeAll = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<MedicalCertificateComponent>,
    private translate: TranslateService,
    private hospitalState: HospitalStateService,
    @Inject(MAT_DIALOG_DATA) private _data: { patientDetails: IPatientInfo, opdDetails: Iopd, ipdData: Iipd },
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private http: HttpClient,
    private userservice: UserService,
    private _medicalCertificateService: MedicalCertificateService,
    private toastr: ToastrService,
    private translationUtils: TranslationUtils,
    private printSettingService: PrintSettingService,
    private defaultValuePipe: DefaultValuePipe
  ) {
    this.patientinfo = _data.patientDetails;
    this.opdDetails = _data.opdDetails;
    this.ipdData = _data.ipdData;
  }

  async ngOnInit(): Promise<void> {
    this.hospitalInfo = this.hospitalState.getHospital();
    // Pronouns
    this.pronouns.subject = this.patientinfo.gender === 'M' ? 'He' : 'She';

    const admissionDate = this.ipdData?.ipdDate
      ? new Date(this.ipdData.ipdDate)
      : new Date();

    const dischargeDate = this.ipdData?.dischargeDate
      ? new Date(this.ipdData.dischargeDate)
      : null;


    this.certificateForm = this.fb.group({
      fromDate: [admissionDate],
      toDate: [dischargeDate],
      diagnosis: [this.ipdData?.finalDiagnosis || ''],
      restOfDays: [1],
      resumeDate: [null],
    });

    this.calculateResumeDate();

    this.certificateForm.get('toDate')?.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      this.calculateResumeDate();
    });

    this.certificateForm.get('restOfDays')?.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      this.calculateResumeDate();
    });

    await this.loadDoctorDetails();
    await this.loadExistingCertificate();
  }

  private async loadExistingCertificate(): Promise<void> {
    if (!this.ipdData.id) {
      return;
    }

    try {
      const response = await this._medicalCertificateService.getMedicalCertificateByIpdId(this.ipdData.id).toPromise();
      // Handle array or single object response
      const certificate = Array.isArray(response) ? (response.length > 0 ? response[0] : null) : response;

      if (certificate) {
        this.certificateForm.patchValue({
          fromDate: certificate.fromDate ? new Date(certificate.fromDate) : null,
          toDate: certificate.toDate ? new Date(certificate.toDate) : null,
          diagnosis: certificate.diagnosis || '',
          restOfDays: certificate.restOfDays || 0,
          resumeDate: certificate.resumeDate ? new Date(certificate.resumeDate) : null,
        });
        this.isSaved = true;
        this.certificateForm.disable();
      }
    } catch (error) {
      console.error('Error loading existing certificate:', error);
    }
  }

  private calculateResumeDate(): void {
    const toDate = this.certificateForm.get('toDate')?.value;
    const restOfDays = Number(this.certificateForm.get('restOfDays')?.value || 0);

    if (!toDate || restOfDays <= 0) {
      this.certificateForm.patchValue({ resumeDate: null });
      return;
    }

    const dischargeDate = new Date(toDate);
    const resumeDate = new Date(dischargeDate);
    resumeDate.setDate(dischargeDate.getDate() + restOfDays);

    this.certificateForm.patchValue({
      resumeDate
    });
  }

  onCloseDialog() {
    this.dialogRef.close();
  }

  async saveAndPrintMedicalCertificate() {
    // Prepare FormData (FORM → BACKEND)
    const existingCertificate = this.certificateForm.getRawValue();

    // Prepare update payload
    const payload: IMedicalCertificate = {
      ...existingCertificate,
      patientId: this.patientinfo.id,
      doctorId: this.ipdData.doctorId,
      ipdId: this.ipdData.id
    };

    // Upload & Save data
    this._medicalCertificateService.saveMedicalCertificate(payload).pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.isSaved = true;
          this.certificateForm.disable();
          const message = this.translationUtils.getTranslatedMessage('COMMON.SAVE', this.translate.instant('BUTTON.BTN_MEDICAL_CERTIFICATE'));
          this.toastr.success(message);
        }
      },
      error: (err) => {
        if (err.error?.message) {
          this.toastr.error(err.error.message);
        } else {
          const allErrors = Object.values(err.error.errors || {}).flat();
          this.toastr.error(allErrors.join('\n'));
        }
      }
    });
  }

  async printCertificate() {
    const content = await this.createPrintContent();
    this.openPrintWindow(content);
  }

  private async createPrintContent(): Promise<string> {
    try {
      let template =
        (await this.http
          .get('assets/print-template.html', { responseType: 'text' })
          .toPromise()) || '';

      const hospitalSafe = this.hospitalState.getHospital();
      const [settings] = await Promise.all([
        this.printSettingService.getPrintSettingPromise(hospitalSafe.id)
      ]);

      if (!this.showPatientInfo) {
        template = template.replace(
          '</head>',
          `<style>
            #showPatientInfo { display: none !important; }
          </style></head>`
        );
      }
      const printContent = document.getElementById('certificate-preview')?.innerHTML;
      const tableHtml = `
        <h2 style="text-align:center; margin-bottom:30px; text-decoration:underline;">
          Medical Certificate
        </h2>
        ${printContent || '<p>No content to display</p>'}
      `;

      const fullImagePath = hospitalSafe.logo ? `${this.staticimagepath}${hospitalSafe.logo}` : null;
      const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;

      const replacements: Record<string, string> = {
        reportTitle: 'Medical Certificate',
        hospitalName: this.defaultValuePipe.transform(hospitalSafe.name, 'Hospital Name'),
        hospitalAddress: this.defaultValuePipe.transform(hospitalSafe.address, ''),
        phone: this.defaultValuePipe.transform(hospitalSafe.phone, ''),
        reportSections: tableHtml,
        doctorName: this.doctorName,
        qualification: this.defaultValuePipe.transform(this.doctorDetails?.degree, ''),
        email: this.doctorDetails?.email || '',
        doctorRegistrationNo: this.doctorDetails?.registrationNumber 
          ? `Reg No. ${this.defaultValuePipe.transform(this.doctorDetails?.registrationNumber, '')}` 
          : '',
        doctorPhone: this.doctorDetails?.phoneNumber 
          ? `M. ${this.defaultValuePipe.transform(this.doctorDetails?.phoneNumber, '')}` 
          : '',
        logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`,
        patientName: `${this.patientinfo.firstName} ${this.patientinfo.lastName}`,
        patientAge: this.defaultValuePipe.transform(this.patientinfo.gender === 'M' ? 'Male' : 'Female', '') + ' / ' + this.defaultValuePipe.transform(this.patientinfo.age, ''),
        opdNumber: this.defaultValuePipe.transform(this.opdDetails.opdNumber, ''),
        opdDate: this.defaultValuePipe.transform(this.opdDetails.opdDate, 'dd-MM-yyyy') || ''
      };

      let filledTemplate = template;
      for (const key in replacements) {
        const value = replacements[key] ?? '';
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        filledTemplate = filledTemplate.replace(regex, value);
      }

      // Apply settings
      if (settings) {
        const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
        const headerClass = settings.logoPosition === 'center' ? 'center' : '';
        const headerAlignment = settings.addressAlignment || 'left';

        const styles = `
            :root {
                --logo-size: ${settings.logoSize || '60px'};
                --logo-position: ${logoPos};
                --header-alignment: ${headerAlignment};
                --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                --address-font-size: ${settings.addressFontSize || 13}px;
                --footer-display: ${settings.showFooter ? 'block' : 'none'};
               
            }
        `;
        filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
      } else {
        filledTemplate = filledTemplate.replace('{{headerClass}}', '');
      }

      return filledTemplate;
    } catch (error) {
      console.error('Error creating medical certificate print content:', error);
      throw error;
    }
  }

  private openPrintWindow(content: string): void {

    let iframe = document.getElementById('print-frame') as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow?.document;

    if (!doc) {
      return;
    }

    doc.open();
    doc.write(content);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
    }, 300);
  }

  get profileImagePathSrc(): string {
    if (this.patientinfo?.profileImagePath) {
      return this.staticimagepath + this.patientinfo.profileImagePath;
    }
    return this.defaultAvatarPath;
  }

  getGenderName(value?: string): string {
    const gender = GenderList.find(g => g.value === value);
    return gender ? gender.name : '';
  }

  async loadDoctorDetails(): Promise<void> {
    const doctorId = this.ipdData?.doctorId;
    if (!doctorId) return;

    const doctor = await this.userservice.getUserByID(doctorId).toPromise();
    if (doctor?.result) {
      this.doctorDetails = doctor?.result;
      this.doctorName = `${this.doctorDetails?.firstName ?? ''} ${this.doctorDetails?.lastName ?? ''}`.trim();
    }
  }
}